import React, { useState } from 'react';
import {
  Fab,
  Drawer,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  IconButton,
  Tooltip,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import ExtensionIcon from '@mui/icons-material/Extension';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TourIcon from '@mui/icons-material/Tour';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';

export default function PrismControlPanel({ 
  open, 
  onToggle,
  onFeatureToggle,
  featuresEnabled: initialFeatures,
  onOpenProtocolSearch,
  onOpenDocAssistant,
  onTriggerNudge,
  timerSpeed = '1x',
  onTimerSpeedChange
}) {
  const [features, setFeatures] = useState(initialFeatures || {
    statusBar: true,
    protocolSearch: true,
    docAssistant: true,
    struggleDetection: false,
    ghostPath: false,
  });
  
  // Update local state when parent state changes
  React.useEffect(() => {
    if (initialFeatures) {
      setFeatures(initialFeatures);
    }
  }, [initialFeatures]);

  const handleFeatureToggle = (feature) => {
    const newState = !features[feature];
    setFeatures({ ...features, [feature]: newState });
    onFeatureToggle && onFeatureToggle(feature, newState);
  };

  const handleCopyURL = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    alert('Demo URL copied to clipboard!');
  };

  const handleReset = () => {
    window.location.href = window.location.pathname + '?demo=prism';
  };

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <Tooltip title="Open PRISM Demo Controls">
          <Fab
            color="primary"
            onClick={onToggle}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 9998,
              bgcolor: 'rgba(18, 24, 54, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(91, 158, 130, 0.5)',
              color: 'rgba(91, 158, 130, 1)',
              '&:hover': {
                bgcolor: 'rgba(18, 24, 54, 1)',
                border: '1px solid rgba(91, 158, 130, 0.8)',
              },
            }}
          >
            <ExtensionIcon />
          </Fab>
        </Tooltip>
      )}

      {/* Control Panel Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        PaperProps={{
          sx: {
            width: 360,
            p: 3,
            pt: 8, // Add top padding to avoid status bar overlap
            bgcolor: 'rgba(18, 24, 54, 0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            backgroundImage: 'none',
            borderLeft: '1px solid rgba(91, 158, 130, 0.3)',
            color: 'white',
            marginTop: '48px', // Offset by status bar height
            height: 'calc(100% - 48px)', // Adjust height to account for status bar
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <ExtensionIcon sx={{ color: 'rgba(91, 158, 130, 1)' }} />
            <Typography variant="h6" fontWeight={600} sx={{ color: 'white' }}>
              PRISM Demo Controls
            </Typography>
          </Box>
          <IconButton size="small" onClick={onToggle} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Feature Toggles */}
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'white' }}>
          Toggle Features
        </Typography>
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column' }}>
          <FormControlLabel
            control={
              <Switch
                checked={features.statusBar}
                onChange={() => handleFeatureToggle('statusBar')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'rgba(91, 158, 130, 1)',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    bgcolor: 'rgba(91, 158, 130, 0.5)',
                  },
                }}
              />
            }
            label={<Typography sx={{ color: 'white' }}>Status Bar</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={features.protocolSearch}
                onChange={() => handleFeatureToggle('protocolSearch')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'rgba(91, 158, 130, 1)',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    bgcolor: 'rgba(91, 158, 130, 0.5)',
                  },
                }}
              />
            }
            label={<Typography sx={{ color: 'white' }}>Protocol Search (Ctrl+K+P)</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={features.docAssistant}
                onChange={() => handleFeatureToggle('docAssistant')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'rgba(91, 158, 130, 1)',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    bgcolor: 'rgba(91, 158, 130, 0.5)',
                  },
                }}
              />
            }
            label={<Typography sx={{ color: 'white' }}>AI Documentation (Ctrl+K+D)</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={features.struggleDetection}
                onChange={() => handleFeatureToggle('struggleDetection')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'rgba(91, 158, 130, 1)',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    bgcolor: 'rgba(91, 158, 130, 0.5)',
                  },
                }}
              />
            }
            label={<Typography sx={{ color: 'white' }}>Struggle Detection</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={features.ghostPath}
                onChange={() => handleFeatureToggle('ghostPath')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'rgba(91, 158, 130, 1)',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    bgcolor: 'rgba(91, 158, 130, 0.5)',
                  },
                }}
              />
            }
            label={<Typography sx={{ color: 'white' }}>Tutorial Ghost Path</Typography>}
          />
        </Box>

        <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Demo Actions */}
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: '#000' }}>
          Demo Actions
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={(e) => onOpenProtocolSearch && onOpenProtocolSearch(e.currentTarget)}
            fullWidth
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'rgba(255,255,255,0.5)' } }}
          >
            Open Protocol Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={(e) => onOpenDocAssistant && onOpenDocAssistant(e.currentTarget)}
            fullWidth
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'rgba(255,255,255,0.5)' } }}
          >
            Open AI Notes
          </Button>
          <Button
            variant="outlined"
            startIcon={<NotificationsActiveIcon />}
            onClick={onTriggerNudge}
            fullWidth
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'rgba(255,255,255,0.5)' } }}
          >
            Trigger Nudge
          </Button>
          <Button
            variant="outlined"
            startIcon={<TourIcon />}
            disabled
            fullWidth
            sx={{ color: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.2)' }}
          >
            Show Ghost Path
          </Button>
        </Box>

        <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Session Timer Speed */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'white' }}>
              Session Timer Speed
            </Typography>
          </FormLabel>
          <RadioGroup value={timerSpeed} onChange={(e) => onTimerSpeedChange && onTimerSpeedChange(e.target.value)}>
            <FormControlLabel 
              value="1x" 
              control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'rgba(91, 158, 130, 1)' } }} />} 
              label={<Typography sx={{ color: 'white' }}>1x (Real-time)</Typography>} 
            />
            <FormControlLabel 
              value="5x" 
              control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'rgba(91, 158, 130, 1)' } }} />} 
              label={<Typography sx={{ color: 'white' }}>5x (Fast)</Typography>} 
            />
            <FormControlLabel 
              value="10x" 
              control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'rgba(91, 158, 130, 1)' } }} />} 
              label={<Typography sx={{ color: 'white' }}>10x (Very Fast)</Typography>} 
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Utility Actions */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyURL}
            fullWidth
            size="small"
            sx={{ 
              bgcolor: 'rgba(91, 158, 130, 0.3)', 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(91, 158, 130, 0.5)' }
            }}
          >
            Copy URL
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            fullWidth
            size="small"
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'rgba(255,255,255,0.5)' } }}
          >
            Reset
          </Button>
        </Box>

        <Typography variant="caption" sx={{ mt: 3, display: 'block', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
          PRISM Demo Mode Active
        </Typography>
      </Drawer>
    </>
  );
}
